import React, { useState } from 'react'

interface LuxuryButtonProps {
  children: React.ReactNode
  onClick?: () => void
  href?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
}

const sizeStyles = {
  sm: { padding: '11px 18px', fontSize: '0.95rem' },
  md: { padding: '15px 34px', fontSize: '1rem' },
  lg: { padding: '18px 42px', fontSize: '1.05rem' },
}

export const LuxuryButton: React.FC<LuxuryButtonProps> = ({
  children,
  onClick,
  href,
  className = '',
  size = 'md',
  fullWidth = false,
  type = 'button',
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const baseStyle: React.CSSProperties = {
    background: 'var(--gradient-gold-button)',
    color: 'var(--text-on-accent)',
    padding: sizeStyles[size].padding,
    fontFamily: 'var(--font-family-ui)',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.01em',
    fontSize: sizeStyles[size].fontSize,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.4s ease',
    position: 'relative',
    clipPath:
      'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)',
    display: fullWidth ? 'flex' : 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    textDecoration: 'none',
    width: fullWidth ? '100%' : 'auto',
    transform: isHovered && !disabled ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: isHovered && !disabled ? 'var(--shadow-button-gold)' : 'none',
    filter: disabled ? 'brightness(0.6)' : isHovered ? 'brightness(1.1)' : 'brightness(1)',
    opacity: disabled ? 0.7 : 1,
  }

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  if (href) {
    return (
      <a
        href={href}
        className={className}
        style={baseStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault()
            onClick()
          }
        }}
      >
        {children}
      </a>
    )
  }

  return (
    <button
      type={type}
      className={className}
      style={baseStyle}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

export default LuxuryButton
